import { type Sensor, sensorSchema } from '@si/types';
import { get, objectToQueryParams } from '@si/utils';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { queryKeys } from './queryKeys';

const responseSchema = z.object({
  sensors: z.array(sensorSchema),
});
const EMPTY_SENSORS: Sensor[] = [];

interface Params {
  projectId: string;
  installationId?: string;
  status?: 'all' | 'active';
  includeReplaced?: boolean;
  enabled?: boolean;
  noRetry?: boolean;
}

export function useSensors({
  projectId,
  installationId,
  status = 'all',
  includeReplaced = false,
  enabled = true,
  noRetry = false,
}: Params) {
  const url = `/projects/${projectId}/sensors${objectToQueryParams({
    installationId,
    status,
    includeReplaced,
  })}`;

  const { data, error, isLoading, refetch, isFetching } = useQuery({
    queryKey: queryKeys.getSensors(
      projectId,
      installationId ?? 'installationId',
      status,
      includeReplaced
    ),
    queryFn: () => get({ url, schema: responseSchema }),
    enabled: !!enabled && !!projectId,
    retry: !noRetry,
  });

  return {
    sensors: data?.sensors ?? EMPTY_SENSORS,
    error,
    isLoading,
    isFetching,
    refetch,
  };
}
